import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Quote } from "lucide-react";
import { getStats, getStories } from "../lib/api";
import { STATS as FALLBACK_STATS } from "../lib/content";

function StatCard({ label, value, i }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ duration: 0.5, delay: i * 0.08 }}
      className="relative border border-white/10 p-8 md:p-10"
      data-testid={`impact-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <p className="font-display text-5xl md:text-6xl font-bold text-[#F5B90A] leading-none">{value}</p>
      <p className="mt-3 text-sm text-white/70 uppercase tracking-widest">{label}</p>
    </motion.div>
  );
}

export default function Impact() {
  const { data: stats = FALLBACK_STATS } = useQuery({ queryKey: ["stats"], queryFn: getStats });
  const { data: stories = [] } = useQuery({ queryKey: ["stories"], queryFn: () => getStories() });

  const items = [
    { label: "Boys Mentored", value: stats.mentored || "—" },
    { label: "Volunteer Mentors", value: stats.volunteers || "—" },
    { label: "School Partnerships", value: stats.schools || "—" },
    { label: "Mentoring Hours", value: stats.hours || "—" },
    { label: "Families Engaged", value: stats.families || "—" },
  ];

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0A0A0A] text-white" data-testid="impact-hero">
        <div className="noise" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#6B21A8]/35 via-transparent to-transparent" />
        <div className="container-x relative py-24 md:py-32">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="gold-line" />
              <span className="overline text-[#F5B90A]">Our Impact</span>
            </div>
            <h1 className="font-display text-5xl md:text-7xl font-bold leading-[1.02] tracking-tight">
              Lives changed. <span className="italic text-[#F5B90A]">Numbers verified.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-white/80">
              Every number here represents a real young man, a real family, and real time our community has invested in his future.
              Statistics are maintained by our team and updated as programs grow.
            </p>
          </div>
        </div>

        <div className="container-x pb-24">
          <div className="grid gap-5 md:grid-cols-5">
            {items.map((s, i) => <StatCard key={s.label} {...s} i={i} />)}
          </div>
        </div>
      </section>

      {/* OUTCOMES */}
      <section className="bg-white py-20 md:py-24" data-testid="impact-outcomes">
        <div className="container-x">
          <div className="max-w-3xl">
            <span className="overline text-[#6B21A8]">What Our Work Produces</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-2">Outcomes we focus on.</h2>
          </div>
          <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {[
              { title: "Youth Skill Development", body: "Communication, decision-making, conflict resolution, and leadership — measured session to session." },
              { title: "Program Participation", body: "Attendance, completion rates, and family engagement across all signature programs." },
              { title: "Mentoring Sessions", body: "Weekly group circles, one-on-one check-ins, and family nights delivered to the community." },
              { title: "School & Community Reach", body: "Classroom partnerships, assemblies, and events across Clayton County and surrounding areas." },
            ].map((o) => (
              <div key={o.title} className="card-editorial p-7">
                <h3 className="font-display text-xl font-bold">{o.title}</h3>
                <p className="mt-3 text-sm text-black/70">{o.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STORIES */}
      {stories.length > 0 && (
        <section className="bg-[#F7F3EC] py-20 md:py-24" data-testid="impact-stories">
          <div className="container-x">
            <div className="flex items-end justify-between flex-wrap gap-3">
              <h2 className="font-display text-4xl md:text-5xl font-bold">Voices from the brotherhood.</h2>
              <Link to="/success-stories" className="text-sm font-semibold text-[#6B21A8] hover:underline">All stories →</Link>
            </div>
            <div className="mt-10 grid gap-6 md:grid-cols-3">
              {stories.slice(0, 3).map((s) => (
                <div key={s.id} className="card-editorial p-7">
                  <Quote className="h-6 w-6 text-[#F5B90A]" />
                  <p className="mt-4 font-display text-lg leading-snug">"{s.quote}"</p>
                  <p className="mt-5 text-sm text-black/60">{s.student_name} · {s.school}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* NOTE */}
      <section className="bg-white py-14" data-testid="impact-note">
        <div className="container-x mx-auto max-w-3xl text-center">
          <p className="text-sm text-black/60">
            To protect the identities of minors, all photos, names, and testimonials used across this site are shared only
            with the approval of the youth and their families. We never invent numbers, testimonials, or program results.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0A0A0A] text-white py-20" data-testid="impact-cta">
        <div className="container-x text-center max-w-3xl mx-auto">
          <h2 className="font-display text-4xl md:text-5xl font-bold">Be part of the next number.</h2>
          <p className="mt-4 text-white/70 text-lg">Enroll your child, mentor a young man, or sponsor our mission.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/enroll" className="btn-gold">Enroll Your Child <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/become-a-mentor" className="btn-outline-white">Become a Mentor</Link>
            <Link to="/donate" className="btn-outline-white">Donate</Link>
          </div>
        </div>
      </section>
    </>
  );
}
